import { useState } from 'react';
import { X, Mail, Eye, EyeOff, ArrowRight, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const USER_TYPES = [
  { id: 'student', label: 'Student', desc: 'College / university student' },
  { id: 'local',   label: 'Local',   desc: 'Athens resident or visitor' },
];

function formatError(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function AuthModal({ onClose, returnTo = '/explore', referredBySlug = '' }) {
  const { login, setUser } = useAuth();
  // mode: login | register | forgot | otp | newpass
  const [mode, setMode] = useState('login');
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'', phone:'', user_type:'student', city:'Athens, GA', school_name:'' });
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState(['','','','','','']);
  const [fpNewPass, setFpNewPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [info,  setInfo]    = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const reset = () => { setError(''); setInfo(''); };

  /* ── Login / Register ── */
  const handleSubmit = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try {
      const url  = mode === 'register' ? `${API}/auth/register` : `${API}/auth/login/email`;
      // For registration, merge prop slug with localStorage fallback (7-day expiry)
      let effectiveSlug = referredBySlug || '';
      if (!effectiveSlug && mode === 'register') {
        const stored = localStorage.getItem('perkly_referral_slug');
        const storedTs = parseInt(localStorage.getItem('perkly_referral_ts') || '0', 10);
        if (stored && (Date.now() - storedTs) < 7 * 24 * 60 * 60 * 1000) {
          effectiveSlug = stored;
        }
      }
      const body = mode === 'register'
        ? { name: form.name, email: form.email, password: form.password, user_type: form.user_type, city: form.city, school_name: form.school_name || null, phone: form.phone || null, referred_by_slug: effectiveSlug || null }
        : { email: form.email, password: form.password };
      const res  = await fetch(url, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail));
      if (mode === 'register') {
        // Clear referral slug after successful signup
        localStorage.removeItem('perkly_referral_slug');
        localStorage.removeItem('perkly_referral_ts');
      }
      setUser(data);
      onClose();
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  /* ── Forgot — send OTP ── */
  const handleForgotSend = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/forgot-password`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ email: fpEmail }) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail));
      setInfo(data.message);
      setMode('otp');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  /* ── Verify OTP ── */
  const otpString = fpOtp.join('');
  const handleOtpInput = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...fpOtp]; next[idx] = val;
    setFpOtp(next);
    if (val && idx < 5) document.getElementById(`otp-${idx+1}`)?.focus();
  };
  const handleVerifyOtp = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/verify-reset-otp`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ email: fpEmail, otp: otpString }) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail));
      setMode('newpass');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  /* ── Reset Password ── */
  const handleResetPassword = async (e) => {
    e.preventDefault(); reset(); setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/reset-password`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ email: fpEmail, otp: otpString, new_password: fpNewPass }) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail));
      if (data.user) setUser(data.user);
      onClose();
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  /* ── Header labels ── */
  const HEADERS = {
    login:    { title: 'Welcome back',          sub: 'Sign in to discover local deals' },
    register: { title: 'Join Perkly',            sub: 'Start discovering deals near you' },
    forgot:   { title: 'Forgot Password?',        sub: 'Enter your email to receive a reset code' },
    otp:      { title: 'Check Your Email',        sub: `We sent a 6-digit code to ${fpEmail}` },
    newpass:  { title: 'Set New Password',        sub: 'Choose a strong password (min 8 characters)' },
  };
  const h = HEADERS[mode] || HEADERS.login;

  return (
    <div className="fixed inset-0 z-[999] bg-[#2B2B2B]/60 backdrop-blur-sm flex items-end sm:items-center sm:p-4" data-testid="auth-modal">
      {/* Tap outside to close */}
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full shadow-2xl overflow-y-auto" style={{ maxWidth: '440px', maxHeight: '92vh', margin: '0 auto' }}>
        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#8A9A8B]/30" />
        </div>

        {/* Header */}
        <div className="bg-[#2B2B2B] px-7 py-6 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{h.title}</h2>
            <p className="text-white/55 text-sm mt-1">{h.sub}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white mt-1 p-1"><X size={20}/></button>
        </div>

        <div className="px-7 py-6 space-y-4">
          {error && <p className="text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2" data-testid="auth-error">{error}</p>}
          {info  && <p className="text-xs text-green-600 bg-green-50 rounded-xl px-3 py-2">{info}</p>}

          {/* ── LOGIN / REGISTER ── */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <button onClick={() => { reset(); login(returnTo); }} data-testid="google-signin-btn"
                className="w-full flex items-center justify-center gap-3 py-3 rounded-2xl border-2 border-[#8A9A8B]/25 hover:border-[#C8A97E] hover:bg-[#C8A97E]/5 transition-all text-sm font-semibold text-[#2B2B2B]">
                <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
                Continue with Google
              </button>
              <div className="flex items-center gap-3"><div className="flex-1 h-px bg-[#8A9A8B]/20"/><span className="text-xs text-[#8A9A8B] font-semibold">or</span><div className="flex-1 h-px bg-[#8A9A8B]/20"/></div>

              <form onSubmit={handleSubmit} className="space-y-3">
                {mode === 'register' && (
                  <>
                    <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your full name" className="perkly-input" data-testid="register-name"/>
                    <div className="grid grid-cols-2 gap-2">
                      {USER_TYPES.map(t => (
                        <label key={t.id} className={`cursor-pointer rounded-xl p-3 border-2 text-center transition-all ${form.user_type===t.id?'border-[#C8A97E] bg-[#C8A97E]/8':'border-[#8A9A8B]/20 hover:border-[#C8A97E]/40'}`}>
                          <input type="radio" name="ut" value={t.id} checked={form.user_type===t.id} onChange={() => set('user_type',t.id)} className="hidden"/>
                          <p className="font-bold text-sm text-[#2B2B2B]">{t.label}</p>
                          <p className="text-xs text-[#2B2B2B]/50 mt-0.5">{t.desc}</p>
                        </label>
                      ))}
                    </div>
                    {form.user_type === 'student' && <input value={form.school_name} onChange={e => set('school_name',e.target.value)} placeholder="School name (optional)" className="perkly-input"/>}
                    <input value={form.phone} onChange={e => set('phone',e.target.value)} placeholder="Mobile number (required)" type="tel" required className="perkly-input" data-testid="register-phone"/>
                  </>
                )}
                <input required type="email" value={form.email} onChange={e => set('email',e.target.value)} placeholder="Email address" className="perkly-input" data-testid="auth-email"/>
                <div className="relative">
                  <input required type={showPw?'text':'password'} value={form.password} onChange={e => set('password',e.target.value)}
                    placeholder={mode==='register'?'Create password (min 8 chars)':'Password'} className="perkly-input pr-11" data-testid="auth-password"/>
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A9A8B] hover:text-[#2B2B2B]">
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>

                {mode === 'login' && (
                  <div className="text-right">
                    <button type="button" onClick={() => { reset(); setFpEmail(form.email); setMode('forgot'); }}
                      className="text-xs text-[#C8A97E] hover:underline font-semibold" data-testid="forgot-password-link">
                      Forgot Password?
                    </button>
                  </div>
                )}

                <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full btn-primary justify-center py-3.5 disabled:opacity-60">
                  {loading
                    ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>{' '}{mode==='register'?'Creating account...':'Signing in...'}</>
                    : <><Mail size={15}/>{' '}{mode==='register'?'Create Account':'Sign In with Email'}</>}
                </button>
              </form>

              <p className="text-center text-sm text-[#2B2B2B]/60">
                {mode === 'login'
                  ? <><span>New to Perkly? </span><button onClick={() => {setMode('register');reset();}} className="text-[#C8A97E] font-semibold hover:underline" data-testid="switch-to-register">Create account</button></>
                  : <><span>Already have an account? </span><button onClick={() => {setMode('login');reset();}} className="text-[#C8A97E] font-semibold hover:underline" data-testid="switch-to-login">Sign in</button></>}
              </p>
            </>
          )}

          {/* ── FORGOT PASSWORD — enter email ── */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotSend} className="space-y-4">
              <input required type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)}
                placeholder="Your registered email" className="perkly-input" data-testid="forgot-email-input"/>
              <button type="submit" disabled={loading} className="w-full btn-primary justify-center py-3.5 disabled:opacity-60" data-testid="forgot-send-btn">
                {loading ? 'Sending...' : <><KeyRound size={15}/> Send Reset Code</>}
              </button>
              <button type="button" onClick={() => {setMode('login');reset();}} className="w-full text-center text-sm text-[#8A9A8B] hover:text-[#2B2B2B]">
                Back to Sign In
              </button>
            </form>
          )}

          {/* ── OTP entry ── */}
          {mode === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="flex gap-2 justify-center">
                {fpOtp.map((d, i) => (
                  <input key={i} id={`otp-${i}`} type="text" inputMode="numeric" maxLength={1} value={d}
                    onChange={e => handleOtpInput(i, e.target.value)}
                    onKeyDown={e => { if (e.key==='Backspace' && !d && i>0) document.getElementById(`otp-${i-1}`)?.focus(); }}
                    className="w-11 h-13 text-center text-xl font-bold rounded-xl border-2 border-[#8A9A8B]/30 focus:border-[#C8A97E] focus:outline-none bg-[#F6F1E9]"
                    style={{height:'52px'}} data-testid={`otp-digit-${i}`}/>
                ))}
              </div>
              <button type="submit" disabled={loading || otpString.length < 6}
                className="w-full btn-primary justify-center py-3.5 disabled:opacity-60" data-testid="otp-verify-btn">
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
              <button type="button" onClick={() => {setMode('forgot');reset();}} className="w-full text-center text-xs text-[#8A9A8B] hover:text-[#2B2B2B]">
                Didn&apos;t get the code? Try again
              </button>
            </form>
          )}

          {/* ── New Password ── */}
          {mode === 'newpass' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="relative">
                <input required type={showPw?'text':'password'} value={fpNewPass} onChange={e => setFpNewPass(e.target.value)}
                  placeholder="New password (min 8 characters)" className="perkly-input pr-11" data-testid="new-password-input"/>
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A9A8B]">
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              <button type="submit" disabled={loading || fpNewPass.length < 8}
                className="w-full btn-primary justify-center py-3.5 disabled:opacity-60" data-testid="reset-password-btn">
                {loading ? 'Saving...' : 'Set New Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
